/**
 * components/inspection/CommissionPanel.js — III ШАТНЫ КОМИСС.
 *
 * ХЭМАБМТ-06 2.5.1 — БН-ийн комисс. ⚠ УДИРДАГЧ нь БН-ИЙН ХАБ
 * МЭРГЭЖИЛТЭН (хэрэглэгчийн шийдвэр 2026-10-26: «БН дарга удирдах нь
 * тохиромжгүй, БН ХАБ удирдах хэрэгтэй»). Журмын 2.5.1-д «БН дарга
 * (ерөнхий инженер)» гэсэн — зөрүүг МЭДСЭЭР сонгов. Гишүүд
 * (ХАБЭА товчооны дарга, инженерүүд, мониторинг инженер, тэргүүлэх эрчим
 * зүйч, механикч, ҮТТ дарга …). 2.5.3 — гишүүн тус бүр өөрийн чиглэлийн
 * хуудастай → энд гишүүн бүрд Хавсралт №4-ийн БҮЛГҮҮДИЙГ оноодог.
 *
 * Хэрэглээ: бөглөх маягтын толгойд, ЗӨВХӨН III шатанд. Удирдагч засна,
 * гишүүд харна. Өмнөх сарын бүрэлдэхүүнийг санал болгоно (журам сард 1).
 *
 * Props:
 *   inspection  — insp (id, bn_code, status)
 *   commission  — insp.commission (members, groups, is_leader …)
 *   onChanged   — хадгалсны дараа маягтыг дахин ачаалах
 */
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Select, Space, Table, Tag, Tooltip, message } from "antd";
import { PlusOutlined, DeleteOutlined, TeamOutlined, HistoryOutlined } from "@ant-design/icons";
import { xabInspectionApi } from "src/api/xabApi";
import WorkerSelect from "./WorkerSelect";

const ROLE = {
  chair: { label: "Удирдагч", color: "gold" },
  member: { label: "Гишүүн", color: "blue" },
};

export default function CommissionPanel({ inspection, commission, onChanged }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [suggest, setSuggest] = useState(null);
  const [commNo, setCommNo] = useState(null);

  const groups = useMemo(() => commission?.groups || [], [commission]);
  const groupOpts = useMemo(
    () => groups.map((g) => ({ value: g.id, label: g.name })),
    [groups],
  );
  const closed = inspection?.status === "closed";
  const canEdit = !!commission?.is_leader && !closed;

  /* ── ХУРААСАН ХАРАГДАЦ (хэрэглэгч 2026-10-30: «шатны комисс гэсэнг
       хураасан харуулах хэрэгтэй») ─────────────────────────────────────
     Комисс нь бөглөх маягтын ТОЛГОЙД сууж, 7-10 гишүүний хүснэгт нь
     дэлгэцийн бараг хагасыг эзэлдэг. Шалгагчийн гол ажил нь доорх
     үзүүлэлтүүд тул комиссыг анхдагчаар ХУРААНА.

     ⚠ ГЭХДЭЭ ДАРААХ ҮЕД ХЭЗЭЭ Ч ХУРААХГҮЙ — эс бөгөөс хэрэглэгч
       хийх ёстой зүйлээ хэзээ ч харахгүй:
         · комисс бүрдээгүй (бүрдүүлэх заавар нь тэндээ байна),
         · бүлгийн оноолтын багана баазад үүсээгүй (анхааруулга),
         · засаж байгаа үед.
     ⚠ ХУРААСАН ҮЕД Ч гишүүний тоо ба «зөвхөн өөрийн бүлэг» гэсэн
       хязгаарлалт нь ТОЛГОЙД харагдана — тэр нь хэрэглэгчийн эрхийг
       хэлж байгаа тул нуувал «яагаад засагдахгүй байна» болно. */
  const [open, setOpen] = useState(false);
  const memberCount = (commission?.members || []).length;
  const mustShow =
    editing || !memberCount || !commission?.group_ids_ready;
  const shown = open || mustShow;

  useEffect(() => {
    setRows((commission?.members || []).map((m) => ({ ...m, _key: m.id || m.tn })));
  }, [commission]);

  // БН = ЦЕХ (tseh_code, А-конвенц); хуучин мөрд bn_code-д байж болно
  const tsehCode = inspection?.tseh_code || inspection?.bn_code;

  // Санал: 1) лавлахын БАЙНГЫН комисс (/incident/reference → «III шатны
  // комисс»), 2) байхгүй бол өмнөх сарын шалгалтын бүрэлдэхүүн.
  // Комисс хоосон үед л санал болгоно (шалгалт үүсэхэд сервер лавлахаас
  // аль хэдийн хуулсан байх тул ихэвчлэн энд хүрэхгүй).
  useEffect(() => {
    if (!tsehCode || (commission?.members || []).length) return;
    xabInspectionApi
      .suggestCommission({ tseh_code: tsehCode })
      .then((d) => setSuggest(d?.members?.length ? d : null))
      .catch(() => {});
  }, [tsehCode, commission?.members]);

  const setRow = (key, patch) =>
    setRows((rs) => rs.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  const addRow = () =>
    setRows((rs) => [
      ...rs,
      { _key: "new-" + Date.now(), tn: null, name: "", member_role: rs.length ? "member" : "chair", group_ids: [] },
    ]);
  const removeRow = (key) => setRows((rs) => rs.filter((r) => r._key !== key));

  const applySuggest = () => {
    setRows((suggest?.members || []).map((m) => ({ ...m, _key: "s-" + m.tn })));
    setEditing(true);
  };

  /* ── ЛАВЛАХААС ШИНЭЧЛЭХ (2026-10-29) ─────────────────────────────
     Хэрэглэгчийн гомдол: «лавлах санд 3 шатны комиссын хийх бүлэг
     сонголт хийсэн байхад шалгах хуудас дээрээ сонголт байхгүй гэж
     гараад байна».

     ⚠ ШАЛТГААН: шалгалтын комисс нь ҮҮСГЭХ ҮЕИЙН ХУУЛБАР. Лавлахын
       дараачийн засвар БАЙГАА шалгалтад хүрдэггүй (хэмжсэн: шалгалт
       08:36-д үүсэхэд ТН 16721-д бүлэг 8,11 байсан; лавлах 15:25-д
       53,11,8,12 болсон ч хуулбар хэвээр).
     ⚠ Гишүүнийг ХАСАХГҮЙ — зөвхөн бүлгийг шинэчилж, дутуу хүнийг
       нэмнэ. Хасах нь «Засах» дээрх ИЛТ үйлдэл хэвээр. */
  const [syncing, setSyncing] = useState(false);
  const syncFromRef = async () => {
    setSyncing(true);
    try {
      const r = await xabInspectionApi.syncCommission(inspection.id);
      const s = r?.synced;
      message.success(
        s
          ? `Лавлахаас шинэчлэв — ${s.updated} гишүүний бүлэг` +
            (s.added ? `, ${s.added} шинэ гишүүн` : "")
          : "Лавлахаас шинэчлэв",
      );
      onChanged?.();
    } catch (e) {
      message.error(
        e?.response?.data?.message || "Лавлахаас шинэчлэхэд алдаа гарлаа",
      );
    } finally {
      setSyncing(false);
    }
  };

  const save = async () => {
    const valid = rows.filter((r) => r.tn);
    if (!valid.length) {
      message.warning("Ядаж нэг гишүүн сонгоно уу");
      return;
    }
    if (!valid.some((r) => r.member_role === "chair")) {
      message.warning(
        "Удирдагчийг заана уу — БН-ийн ХАБ мэргэжилтэн " +
          "(байгууллагын шийдвэр 2026-10-26)"
      );
      return;
    }
    setSaving(true);
    try {
      await xabInspectionApi.setCommission(inspection.id, {
        members: valid.map((r) => ({
          tn: r.tn,
          name: r.name,
          member_role: r.member_role,
          group_ids: r.group_ids || [],
        })),
        ...(commNo ? { commission_no: commNo } : {}),
      });
      message.success("Комисс хадгалагдлаа");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      message.error(e?.response?.data?.message || "Хадгалахад алдаа гарлаа");
    } finally {
      setSaving(false);
    }
  };

  // Оноогдоогүй бүлгүүд — удирдагчид сануулна
  const assigned = new Set(rows.flatMap((r) => r.group_ids || []));
  const unassigned = groups.filter((g) => !assigned.has(g.id));

  const cols = [
    {
      title: "Гишүүн",
      render: (_v, r) =>
        editing ? (
          <WorkerSelect
            tsehCode={tsehCode}
            value={r.tn || undefined}
            initialWorker={r.tn ? { tn: r.tn, name: r.name } : null}
            onChange={(tn, w) => setRow(r._key, { tn, name: w?.name || "", position_name: w?.position })}
          />
        ) : (
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 600 }}>{r.name || r.tn}</div>
            <div style={{ fontSize: 11, color: "#8A94A6" }}>{r.position_name}</div>
          </div>
        ),
    },
    {
      title: "Үүрэг",
      width: 130,
      render: (_v, r) =>
        editing ? (
          <Select
            size="small"
            value={r.member_role || "member"}
            onChange={(v) => setRow(r._key, { member_role: v })}
            options={[
              { value: "chair", label: "Удирдагч" },
              { value: "member", label: "Гишүүн" },
            ]}
            style={{ width: "100%" }}
          />
        ) : (
          <Tag color={ROLE[r.member_role]?.color}>{ROLE[r.member_role]?.label || r.member_role}</Tag>
        ),
    },
    {
      title: "Хариуцах бүлэг (хавс. №4)",
      render: (_v, r) =>
        editing ? (
          <Select
            size="small"
            mode="multiple"
            placeholder="Бүлэг сонгох"
            value={r.group_ids || []}
            onChange={(v) => setRow(r._key, { group_ids: v })}
            options={groupOpts}
            style={{ width: "100%" }}
            maxTagCount="responsive"
          />
        ) : (r.group_ids || []).length ? (
          <Space size={[4, 4]} wrap>
            {(r.group_ids || []).map((gid) => (
              <Tag key={gid} style={{ marginInlineEnd: 0 }}>
                {groups.find((g) => g.id === gid)?.name || gid}
              </Tag>
            ))}
          </Space>
        ) : (
          <span style={{ color: "#C3C9D4" }}>—</span>
        ),
    },
    ...(editing
      ? [
          {
            title: "",
            width: 40,
            render: (_v, r) => (
              <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removeRow(r._key)} />
            ),
          },
        ]
      : []),
  ];

  return (
    <Card
      size="small"
      style={{ marginBottom: 12, borderColor: "#D9E2EC" }}
      /* ⚠ ХУРААСАН үед БИЕИЙГ бүхэлд нь хаана. `bodyStyle` ЗААВАЛ:
           зөвхөн агуулгыг `null` болговол antd нь биеийн 12px
           дотор зайг хэвээр зурж, хоосон тууз үлдэнэ. */
      bodyStyle={shown ? undefined : { display: "none" }}
      title={
        <span
          style={{
            fontSize: 13,
            cursor: mustShow ? "default" : "pointer",
            userSelect: "none",
          }}
          onClick={() => !mustShow && setOpen((o) => !o)}
          title={mustShow ? undefined : "Дарж хураах / дэлгэх"}
        >
          {/* ⚠ Зөвхөн ХУРААХ боломжтой үед сум гарна — хураагдахгүй
              үед сум нь дарахад хариу өгөхгүй тул хэрэглэгчийг
              төөрөгдүүлнэ. */}
          {mustShow ? null : (
            <i
              className={shown ? "fas fa-chevron-down" : "fas fa-chevron-right"}
              style={{ marginRight: 6, fontSize: 10, color: "#8A94A6" }}
            />
          )}
          <TeamOutlined /> III шатны комисс
          {shown ? (
            <span style={{ fontWeight: 400, color: "#8A94A6", marginLeft: 8, fontSize: 11.5 }}>
              БН-ийн ХАБ мэргэжилтэн удирдана · гишүүн бүр өөрийн бүлгийг бөглөнө (2.5.3)
            </span>
          ) : (
            /* ХУРААСАН ТОЛГОЙ — гишүүний тоо ба МИНИЙ хязгаарлалт. */
            <span style={{ fontWeight: 400, color: "#8A94A6", marginLeft: 8, fontSize: 11.5 }}>
              {memberCount} гишүүн
              {commission?.own_groups_only
                ? " · та зөвхөн өөрийн бүлгийг бөглөнө"
                : ""}
            </span>
          )}
        </span>
      }
      extra={
        canEdit ? (
          editing ? (
            <Space size={6}>
              <Button size="small" onClick={() => { setEditing(false); setRows((commission?.members || []).map((m) => ({ ...m, _key: m.id || m.tn }))); }}>
                Болих
              </Button>
              <Button size="small" type="primary" loading={saving} onClick={save}>
                Хадгалах
              </Button>
            </Space>
          ) : (
            <Space size={6}>
              {/* ⚠ Зөвхөн бүрэлдэхүүн БАЙГАА үед: хоосон комиссыг
                  лавлахаас бүрдүүлэх нь «Комисс бүрдүүлэх» дээрх
                  суулт (suggest) хэвээр. */}
              {rows.length ? (
                <Button
                  size="small"
                  loading={syncing}
                  onClick={syncFromRef}
                  title="Лавлах сан дээрх бүлгийн оноолтыг татаж шинэчилнэ. Гишүүн хасагдахгүй."
                >
                  Лавлахаас шинэчлэх
                </Button>
              ) : null}
              <Button size="small" onClick={() => setEditing(true)}>
                {rows.length ? "Засах" : "Комисс бүрдүүлэх"}
              </Button>
            </Space>
          )
        ) : null
      }
    >
      {/* ⚠ ХУРААСАН үед БИЕ бүхэлдээ рендэрлэгдэхгүй — 7-10 гишүүний
          хүснэгт, саналын Alert, хуваарилалтын Select бүгд. */}
      {!shown ? null : (
        <>
      {!commission?.group_ids_ready ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8, fontSize: 12 }}
          message="Бүлгийн оноолтын багана баазад үүсээгүй байна — бүрэлдэхүүн хадгалагдана, бүлгийн хуваарилалт алгасагдана"
        />
      ) : null}

      {suggest && !rows.length && canEdit ? (
        <Alert
          type="info"
          showIcon
          icon={<HistoryOutlined />}
          style={{ marginBottom: 8 }}
          message={
            suggest.source?.kind === "reference"
              ? `Лавлахад бүртгэсэн БН-ийн байнгын комисс — ${suggest.members.length} гишүүн`
              : `Өмнөх шалгалтын (${suggest.source?.code || ""}) комисс — ${suggest.members.length} гишүүн`
          }
          action={
            <Button size="small" type="primary" ghost onClick={applySuggest}>
              {suggest.source?.kind === "reference" ? "Лавлахаас авах" : "Ижил бүрэлдэхүүнээр"}
            </Button>
          }
        />
      ) : null}

      {editing ? (
        <div style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Button size="small" icon={<PlusOutlined />} onClick={addRow}>
            Гишүүн нэмэх
          </Button>
          {/* 2.5.2 — тархсан объекттой нэгжид 2 комисс зэрэг */}
          <Tooltip title="Журам 2.5.2 — Ил уурхай, Баяжуулах үйлдвэр, Автотээврийн цех, Ус хангамжийн цех: 2 комисс зэрэг. Удирдагч нь БН-ийн ХАБ мэргэжилтэн (байгууллагын шийдвэр 2026-10-26; журамд «БН дарга / ерөнхий инженер» гэсэн)">
            <Select
              size="small"
              allowClear
              placeholder="Комисс №"
              value={commNo}
              onChange={setCommNo}
              options={[
                { value: 1, label: "Комисс №1" },
                { value: 2, label: "Комисс №2" },
              ]}
              style={{ width: 130 }}
            />
          </Tooltip>
          {unassigned.length ? (
            <span style={{ fontSize: 11.5, color: "#B45309" }}>
              Оноогоогүй бүлэг: {unassigned.map((g) => g.name).join(" · ")}
            </span>
          ) : null}
        </div>
      ) : null}

      {rows.length ? (
        <Table
          rowKey="_key"
          size="small"
          pagination={false}
          columns={cols}
          dataSource={rows}
        />
      ) : !editing ? (
        <div style={{ fontSize: 12, color: "#8A94A6" }}>
          Комисс бүрдээгүй — шалгалтыг нэг хүн бүхэлд нь бөглөнө.
          {canEdit
            ? " «Комисс бүрдүүлэх» дарж гишүүдэд бүлэг оноож болно. Байнгын комиссыг лавлахад (Хяналт шалгалтын лавлах → III шатны комисс) бүртгэвэл дараагийн шалгалтад автоматаар орно."
            : ""}
        </div>
      ) : null}

      {/* ⚠ ХОЁР ӨӨР ЗҮЙЛ (2026-10-28). Өмнө нь хоёуланг `restricted`-ээр
          нэг дор хэлдэг байв. Гэтэл шалгалтыг ӨӨРӨӨ үүсгэсэн ердийн
          гишүүн нь бүлгээрээ хязгаарлагдана (`own_groups_only`) ч
          шалгалтаа хааж ЧАДНА (`restricted = false`) — тэр хүнд
          мэдэгдэл огт гарахгүй байв. */}
      {commission?.own_groups_only || commission?.restricted ? (
        <div style={{ marginTop: 8, fontSize: 12, color: "#0F6E56" }}>
          <i className="fas fa-info-circle" />{" "}
          {commission?.own_groups_only
            ? "Та комиссын гишүүн — зөвхөн өөрт оноогдсон бүлгийг бөглөнө."
            : ""}
          {commission?.restricted ? " Шалгалтыг удирдагч дүгнэж хаана." : ""}
        </div>
      ) : null}
        </>
      )}
    </Card>
  );
}
